import * as XLSX from 'xlsx'
import { useState } from 'react'
import { Download } from 'lucide-react'
import { Button, Pagination } from '../../../../components'
import { AVATAR_COLOR_CLASSES } from '../constants/hrmsAttendanceLeave.constants'
import {
  calculateDays,
  formatDateDisplay,
} from '../constants/hrmsLeaveRequests.constants'
import type { StaffLeaveRequest } from '../types/hrmsLeaveRequests.types'
import { LeaveStatusPill } from './LeaveStatusPill'
import { LeaveTypeLabel } from './LeaveTypeLabel'

interface LeaveRequestsTableProps {
  requests: StaffLeaveRequest[]
  onReviewLeave: (id: string) => void
  onViewLeave: (id: string) => void
  onExportDailySheet?: () => void
  page?: number
  limit?: number
  total?: number
  onPageChange?: (page: number) => void
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

export function LeaveRequestsTable({
  requests,
  onReviewLeave,
  onViewLeave,
  onExportDailySheet,
  page,
  limit,
  total,
  onPageChange,
}: LeaveRequestsTableProps) {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const isServerPagination = typeof onPageChange === 'function' && typeof total === 'number'

  const effectivePage = isServerPagination ? page || 1 : currentPage
  const effectivePageSize = isServerPagination ? limit || 10 : 5
  const totalCount = isServerPagination ? total ?? 0 : requests.length
  const totalPages = Math.ceil(totalCount / effectivePageSize) || 1
  const safePage = Math.min(Math.max(1, effectivePage), totalPages)

  const from = totalCount === 0 ? 0 : (safePage - 1) * effectivePageSize + 1
  const to = Math.min(safePage * effectivePageSize, totalCount)
  const paginatedRequests = isServerPagination
    ? requests
    : requests.slice((safePage - 1) * effectivePageSize, safePage * effectivePageSize)
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)

  const handlePageChange = (newPage: number) => {
    if (isServerPagination) {
      onPageChange?.(newPage)
    } else {
      setCurrentPage(newPage)
    }
  }

  const handleExport = () => {
    if (onExportDailySheet) {
      onExportDailySheet()
      return
    }
    const todayStr = new Date().toISOString().split('T')[0]
    const fileName = `leave-requests-${todayStr}.xlsx`

    const exportRows = requests.map((r) => {
      const daysVal = parseInt(calculateDays(r.from, r.to), 10) || 1
      return {
        'Employee': r.name,
        'Employee ID': r.employeeId,
        'Department': r.department,
        'Leave Type': r.leaveType,
        'From': formatDateDisplay(r.from),
        'To': formatDateDisplay(r.to),
        'Days': daysVal,
        'Status': r.status,
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(exportRows)

    worksheet['!cols'] = [
      { wch: 24 },
      { wch: 16 },
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 10 },
      { wch: 14 },
    ]

    worksheet['!views'] = [{ state: 'frozen', ySplit: 1 }]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leave Requests')
    XLSX.writeFile(workbook, fileName)
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs min-w-0 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-slate-900">Leave Requests</h2>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<Download className="h-4 w-4" />}
          onClick={handleExport}
          className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-9 text-xs rounded-lg font-semibold shadow-xs whitespace-nowrap shrink-0 self-start sm:self-auto"
        >
          Export Daily Sheet
        </Button>
      </div>

      <div className="w-full overflow-x-auto min-w-0 rounded-xl border border-slate-200">
        <table className="w-full min-w-max text-left text-sm border-collapse table-auto">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold border-b border-slate-200">
              <th className="py-3 px-4 whitespace-nowrap">EMPLOYEE</th>
              <th className="py-3 px-4 whitespace-nowrap">EMPLOYEE ID</th>
              <th className="py-3 px-4 whitespace-nowrap">LEAVE TYPE</th>
              <th className="py-3 px-4 whitespace-nowrap">FROM</th>
              <th className="py-3 px-4 whitespace-nowrap">TO</th>
              <th className="py-3 px-4 whitespace-nowrap">DAYS</th>
              <th className="py-3 px-4 whitespace-nowrap">STATUS</th>
              <th className="py-3 px-4 text-right whitespace-nowrap">ACTION</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedRequests.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-slate-500 font-medium whitespace-nowrap">
                  No leave requests found
                </td>
              </tr>
            ) : (
              paginatedRequests.map((req, index) => {
                const colorClass = AVATAR_COLOR_CLASSES[index % AVATAR_COLOR_CLASSES.length]
                const isPending = req.status === 'Pending'

                return (
                  <tr key={req.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${colorClass}`}
                        >
                          {getInitials(req.name)}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 whitespace-nowrap">{req.name}</div>
                          {req.department && req.department !== '-' && (
                            <div className="text-xs text-slate-500 whitespace-nowrap">{req.department}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">{req.employeeId}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <LeaveTypeLabel leaveType={req.leaveType} />
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">
                      {formatDateDisplay(req.from)}
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">
                      {formatDateDisplay(req.to)}
                    </td>
                    <td className="py-3 px-4 font-bold text-slate-900 whitespace-nowrap">
                      {calculateDays(req.from, req.to)}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <LeaveStatusPill status={req.status} />
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {isPending ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onReviewLeave(req.id)}
                          className="h-7 px-3 text-xs"
                        >
                          Review
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onViewLeave(req.id)}
                          className="h-7 px-3 text-xs border-none bg-transparent hover:bg-slate-100 text-slate-600 font-semibold"
                        >
                          View
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="pt-2">
        <Pagination
          summary={
            <span>
              Showing <span className="font-bold">{from}</span>–<span className="font-bold">{to}</span> of{' '}
              <span className="font-bold">{totalCount}</span> requests
            </span>
          }
          currentPage={safePage}
          totalPages={totalPages}
          pages={pageNumbers}
          previousDisabled={safePage <= 1}
          nextDisabled={safePage >= totalPages || totalPages === 0}
          onPrevious={() => handlePageChange(Math.max(1, safePage - 1))}
          onNext={() => handlePageChange(Math.min(totalPages, safePage + 1))}
          onPageChange={(p) => handlePageChange(p)}
        />
      </div>
    </div>
  )
}
